import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Chart from '@/components/Base/Chart';
import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

// ── Helpers ────────────────────────────────────────────────────────────────
const DASHBOARD_DATE_RANGE_STORAGE_KEY = 'npdsurvey:admin:dashboardDateRange';
const DASHBOARD_CONTACT_MODE_STORAGE_KEY = 'npdsurvey:admin:dashboardContactMode';
const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO:   dayjs(today).endOf('day').toISOString(),
  };
}

function shouldAutoRefresh(range: { startISO: string; endISO: string }): boolean {
  const start = dayjs(range.startISO);
  const end = dayjs(range.endISO);
  if (!start.isValid() || !end.isValid()) return false;
  return end.diff(start, 'day') <= 31;
}

function formatPieLabel(value: number, total: number, mode: 'percentage' | 'value') {
  if (!value) return '';
  if (mode === 'value') return NUMBER_FORMATTER.format(value);
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  return `${pct}%`;
}

function formatLegendMetric(
  count: number,
  total: number,
  mode: 'percentage' | 'value'
): string {
  const countText = NUMBER_FORMATTER.format(count);
  if (mode !== 'percentage') return countText;
  return `${countText}\u00A0(${formatPieLabel(count, total, 'percentage')})`;
}

function formatNumberTick(value: number | string): string | number {
  if (typeof value === 'number') return NUMBER_FORMATTER.format(value);
  return value;
}

function buildNiceAxis(values: number[], targetSteps = 5) {
  const maxValue = Math.max(0, ...values.map((v) => Number(v) || 0));
  if (maxValue <= 0) {
    return { max: 5, stepSize: 1 };
  }

  const roughStep = maxValue / targetSteps;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  let niceNormalized = 1;
  if (normalized > 1 && normalized <= 2) niceNormalized = 2;
  else if (normalized > 2 && normalized <= 5) niceNormalized = 5;
  else if (normalized > 5) niceNormalized = 10;

  const stepSize = niceNormalized * magnitude;
  const max = Math.ceil(maxValue / stepSize) * stepSize;

  return { max, stepSize };
}

function getIsoWeekInfo(dateStr: string) {
  const [yearRaw, monthRaw, dayRaw] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(yearRaw, monthRaw - 1, dayRaw));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  const key = `${isoYear}-${String(week).padStart(2, '0')}`;
  const label = `W${week} ${isoYear}`;
  return { key, label };
}

// ── Colour palette ─────────────────────────────────────────────────────────

const COLORS = [
  '#93c5fd','#86efac','#fcd34d','#fca5a5','#c4b5fd',
  '#f9a8d4','#6ee7b7','#fdba74','#a5f3fc','#d9f99d',
  '#fbcfe8','#bfdbfe','#bbf7d0','#fef08a','#fecaca',
];

function hexAlpha(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Shared datalabels config ───────────────────────────────────────────────

const BAR_DATALABELS = {
  display: true,
  anchor: 'end' as const,
  align: 'end' as const,
  offset: 2,
  color: '#1e293b',
  font: { size: 9, weight: 600 as const },
  formatter: (value: number) => (value > 0 ? NUMBER_FORMATTER.format(value) : ''),
};

const STACKED_DATALABELS = {
  display: true,
  anchor: 'center' as const,
  align: 'center' as const,
  color: '#1e293b',
  font: { size: 8, weight: 600 as const },
  formatter: (value: number) => (value > 0 ? NUMBER_FORMATTER.format(value) : ''),
};

// ── Shared chart.js option helpers ─────────────────────────────────────────

const BASE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 500 },
  plugins: {
    legend: {
      labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 }, padding: 8 },
    },
    tooltip: { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 8, cornerRadius: 8 },
  },
  scales: {
    x: {
      ticks: { font: { size: 10 }, maxRotation: 0 },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
    y: {
      ticks: { font: { size: 10 } },
      grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
      border: { display: false },
    },
  },
};

// ── Types ──────────────────────────────────────────────────────────────────

type NewVsExistingPoint = { date: string; newUsers: number; existingUsers: number };
type RedemptionByMonth  = { month: string; count: number };
type BrandShare         = { brand: string; count: number };
type AgePoint           = { age: string; count: number };
type SmokerProfileSlice = { label: string; count: number };
type SmokerProfileCompany = {
  company: string;
  brands: SmokerProfileSlice[];
  categories: SmokerProfileSlice[];
};

type DashboardStats = {
  totalCustomers:          number;
  uniqueContactCount:      number;
  activeContactCount:      number;
  tngUsed:                 number;
  tngTotal:                number;
  tngPct:                  number;
  newVsExisting:           NewVsExistingPoint[];
  redemptionByMonth:       RedemptionByMonth[];
  brandShare:              BrandShare[];
  smokerProfileByCompany:  SmokerProfileCompany[];
  unlimitedPuffFeedback:   { convenientRelevant: number; notRelevant: number };
  consumerEvoDailyUsage:   Array<{ label: string; count: number }>;
  deviceRegistrationFeedback: Array<{ label: string; count: number }>;
  ploomExperience:         Array<{ label: string; count: number }>;
  oosAreaCounts:           Array<{ zone: string; count: number }>;
  redemptionByDistributor: Record<string, number | string>[];
  distributors:            string[];
  ageDistribution:         AgePoint[];
  redemptionByStateRace:   Record<string, number | string>[];
  races:                   string[];
};

const DASHBOARD_REFRESH_MS = 1 * 60 * 1000;

// ── Component ──────────────────────────────────────────────────────────────

export default function Home() {
  const readInitialDateRange = () => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(DASHBOARD_DATE_RANGE_STORAGE_KEY);
    const value = (raw || '').trim();
    return value.length > 0 ? value : null;
  };

  const toIsoRange = (range: string) => {
    const raw = (range || '').trim();
    if (!raw) return null;
    const [startRaw, endRaw] = raw.split(' - ');
    const startStr = (startRaw || '').trim();
    const endStr = ((endRaw || '') || startStr).trim();
    if (!startStr) return null;
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    if (!start.isValid() || !end.isValid()) return null;
    return {
      startISO: start.startOf('day').toISOString(),
      endISO: end.endOf('day').toISOString(),
    };
  };

  const initialSavedRange = readInitialDateRange();
  const readInitialContactMode = (): 'unique' | 'active' | 'both' => {
    if (typeof window === 'undefined') return 'unique';
    const raw = window.localStorage.getItem(DASHBOARD_CONTACT_MODE_STORAGE_KEY);
    if (raw === 'both') return 'both';
    return raw === 'active' ? 'active' : 'unique';
  };

  const todayStr = todayMYT();
  const [dateRange, setDateRange] = useState(initialSavedRange ?? `${todayStr} - ${todayStr}`);
  const [contactMode, setContactMode] = useState<'unique' | 'active' | 'both'>(readInitialContactMode);
  const [smokerProfileLabelMode, setSmokerProfileLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [ethnicLabelMode, setEthnicLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [ageLabelMode, setAgeLabelMode] = useState<'percentage' | 'value'>('value');
  const [unlimitedPuffLabelMode, setUnlimitedPuffLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [deviceRegLabelMode, setDeviceRegLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [evoUsageLabelMode, setEvoUsageLabelMode] = useState<'percentage' | 'value'>('value');
  const [ploomExperienceLabelMode, setPloomExperienceLabelMode] = useState<'percentage' | 'value'>('percentage');
  const [confirmedRange, setConfirmedRange] = useState<{ startISO: string; endISO: string } | null>(
    (initialSavedRange && toIsoRange(initialSavedRange)) || todayISORange()
  );
  const [stats,   setStats]   = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dashboardExportRef = useRef<HTMLDivElement | null>(null);

  const dateRangeISO = useMemo(() => {
    const raw = (dateRange || '').trim();
    if (!raw) return null;
    const [startRaw, endRaw] = raw.split(' - ');
    const startStr = (startRaw || '').trim();
    const endStr   = ((endRaw || '') || startStr).trim();
    if (!startStr) return null;
    const start = dayjs(startStr);
    const end   = dayjs(endStr);
    if (!start.isValid() || !end.isValid()) return null;
    return { startISO: start.startOf('day').toISOString(), endISO: end.endOf('day').toISOString() };
  }, [dateRange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = (dateRange || '').trim();
    if (!value) return;
    window.localStorage.setItem(DASHBOARD_DATE_RANGE_STORAGE_KEY, value);
  }, [dateRange]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DASHBOARD_CONTACT_MODE_STORAGE_KEY, contactMode);
  }, [contactMode]);

  const handleSearch = () => {
    if (!dateRangeISO) return;
    setConfirmedRange({ ...dateRangeISO });
  };

  const isUniqueSelected = contactMode === 'unique' || contactMode === 'both';
  const isActiveSelected = contactMode === 'active' || contactMode === 'both';

  const handleUniqueToggle = (checked: boolean) => {
    if (checked) {
      setContactMode(isActiveSelected ? 'both' : 'unique');
      return;
    }
    // prevent empty selection
    if (isActiveSelected) setContactMode('active');
  };

  const handleActiveToggle = (checked: boolean) => {
    if (checked) {
      setContactMode(isUniqueSelected ? 'both' : 'active');
      return;
    }
    // prevent empty selection
    if (isUniqueSelected) setContactMode('unique');
  };

  const handleExportToPdf = useCallback(async () => {
    if (!dashboardExportRef.current || loading || isExportingPdf || !stats) return;

    setIsExportingPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 5;
      const marginY = 10;
      const printableWidth = pageWidth - marginX * 2;
      const printableHeight = pageHeight - marginY * 2;
      const exportRangeStart = confirmedRange ? dayjs(confirmedRange.startISO) : null;
      const exportRangeEnd = confirmedRange ? dayjs(confirmedRange.endISO) : null;
      const rangeLabel =
        exportRangeStart && exportRangeEnd
          ? `${exportRangeStart.format('DD MMM YYYY')} - ${exportRangeEnd.format('DD MMM YYYY')}`
          : (dateRange || '').trim();
      const captureTarget = dashboardExportRef.current;
      const exportBlocks = Array.from(
        captureTarget.querySelectorAll<HTMLElement>('[data-pdf-block="true"]')
      ).filter((element) => element.offsetParent !== null);

      if (exportBlocks.length === 0) {
        throw new Error('No visible dashboard sections to export');
      }

      pdf.setFontSize(12);
      pdf.setTextColor(30, 41, 59);
      pdf.text(`Date Range: ${rangeLabel}`, marginX, marginY);

      let cursorY = marginY + 8;
      const sectionGap = 6;
      const waitForStableRender = async (delayMs = 250) => {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.setTimeout(() => resolve(), delayMs);
            });
          });
        });
      };

      // Keep a short settle delay for consistent chart capture without long waits.
      await waitForStableRender(120);

      for (let i = 0; i < exportBlocks.length; i += 1) {
        const block = exportBlocks[i];
        const rect = block.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) continue;

        const canvas = await html2canvas(block, {
          // Lower scale significantly improves export speed with acceptable readability.
          scale: 1.35,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height),
          windowWidth: document.documentElement.clientWidth,
          windowHeight: document.documentElement.clientHeight,
          scrollX: -window.scrollX,
          scrollY: -window.scrollY,
          logging: false,
          onclone: (doc) => {
            // Avoid clipping on export-only render tree.
            doc
              .querySelectorAll<HTMLElement>(
                '[data-pdf-block="true"], .overflow-x-auto, .overflow-y-auto'
              )
              .forEach((el) => {
                el.style.overflow = 'visible';
                el.style.maxWidth = 'none';
                el.style.maxHeight = 'none';
              });
          },
        });

        const baseWidth = printableWidth;
        const baseHeight = (canvas.height * baseWidth) / canvas.width;
        const scaleToFitHeight = baseHeight > printableHeight ? printableHeight / baseHeight : 1;
        const renderWidth = baseWidth * scaleToFitHeight;
        const renderHeight = baseHeight * scaleToFitHeight;

        if (i > 0 && cursorY + renderHeight > pageHeight - marginY) {
          pdf.addPage();
          cursorY = marginY;
        }

        const x = marginX + (printableWidth - renderWidth) / 2;
        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        pdf.addImage(imgData, 'JPEG', x, cursorY, renderWidth, renderHeight, undefined, 'FAST');
        cursorY += renderHeight + sectionGap;
      }

      const safeRange = (dateRange || '')
        .replace(/\s+/g, '_')
        .replace(/[^0-9_-]/g, '')
        .slice(0, 40);
      const fallbackDate = dayjs().format('YYYYMMDD');
      const filename = `dashboard_${safeRange || fallbackDate}_${contactMode}.pdf`;
      pdf.save(filename);
    } catch (err: unknown) {
      setError(err instanceof Error ? `Failed to export PDF: ${err.message}` : 'Failed to export PDF');
    } finally {
      setIsExportingPdf(false);
    }
  }, [confirmedRange, contactMode, dateRange, isExportingPdf, loading, stats]);

  const fetchDashboardStats = useCallback(
    async (
      range: { startISO: string; endISO: string },
      options?: { background?: boolean }
    ) => {
      const background = options?.background ?? false;
      if (background && abortRef.current) return;

      abortRef.current?.abort();
      if (!background) {
        setIsRefreshing(false);
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      if (background) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const origin = getApiOrigin();
      const params = new URLSearchParams({
        startDate: range.startISO,
        endDate: range.endISO,
        contactMode,
      });

      try {
        const response = await fetch(`${origin}/api/v1/admin/dashboard?${params.toString()}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = (await response.json()) as { ok: boolean; data: DashboardStats };
        setStats(json.data);
        setLastUpdatedAt(new Date());
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        const isActiveRequest = abortRef.current === controller;
        if (isActiveRequest) {
          abortRef.current = null;
        }
        if (background && isActiveRequest) {
          setIsRefreshing(false);
        } else if (!background && isActiveRequest) {
          setLoading(false);
        }
      }
    },
    [contactMode]
  );

  useEffect(() => {
    if (!confirmedRange) return;
    void fetchDashboardStats(confirmedRange);
  }, [confirmedRange, fetchDashboardStats, contactMode]);

  useEffect(() => {
    if (!confirmedRange) return;
    if (!shouldAutoRefresh(confirmedRange)) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchDashboardStats(confirmedRange, { background: true });
    }, DASHBOARD_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [confirmedRange, fetchDashboardStats]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // ── Chart data memos ────────────────────────────────────────────────────

  const weeklyUserSeries = useMemo(() => {
    const source = stats?.newVsExisting ?? [];
    const weekMap = new Map<string, { label: string; newUsers: number; existingUsers: number }>();
    for (const row of source) {
      const info = getIsoWeekInfo(row.date);
      if (!weekMap.has(info.key)) {
        weekMap.set(info.key, {
          label: info.label,
          newUsers: 0,
          existingUsers: 0,
        });
      }
      const bucket = weekMap.get(info.key)!;
      bucket.newUsers += row.newUsers;
      bucket.existingUsers += row.existingUsers;
    }
    return [...weekMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, value]) => value);
  }, [stats?.newVsExisting]);

  const newUsersByWeekData = useMemo(() => ({
    labels: weeklyUserSeries.map((p) => p.label),
    datasets: [{
      label: 'New Users',
      data: weeklyUserSeries.map((p) => p.newUsers),
      backgroundColor: '#ef4444',
      borderColor: '#dc2626',
      borderWidth: 1,
      borderRadius: 5,
      borderSkipped: false,
      barThickness: 20,
      maxBarThickness: 24,
    }],
  }), [weeklyUserSeries]);

  const existingUsersByWeekData = useMemo(() => ({
    labels: weeklyUserSeries.map((p) => p.label),
    datasets: [{
      label: 'Existing Users',
      data: weeklyUserSeries.map((p) => p.existingUsers),
      backgroundColor: '#93c5fd',
      borderColor: '#60a5fa',
      borderWidth: 1,
      borderRadius: 5,
      borderSkipped: false,
      barThickness: 20,
      maxBarThickness: 24,
    }],
  }), [weeklyUserSeries]);

  const weeklyUsersBarOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: {
          ...BASE_OPTIONS.scales.x.ticks,
          maxRotation: 45,
          minRotation: 35,
        },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  const redemptionByMonthData = useMemo(() => {
    const d = stats?.redemptionByMonth ?? [];
    return {
      labels: d.map((p) => p.month),
      datasets: [{
        label: 'Redeemed',
        data: d.map((p) => p.count),
        backgroundColor: '#c4b5fd',
        borderColor: '#a78bfa',
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.redemptionByMonth]);

  const redemptionByMonthAxis = useMemo(
    () => buildNiceAxis((stats?.redemptionByMonth ?? []).map((p) => p.count)),
    [stats?.redemptionByMonth]
  );

  const redemptionByMonthOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      y: {
        ...BASE_OPTIONS.scales.y,
        beginAtZero: true,
        max: redemptionByMonthAxis.max,
        ticks: {
          ...BASE_OPTIONS.scales.y.ticks,
          stepSize: redemptionByMonthAxis.stepSize,
          callback: formatNumberTick,
        },
      },
    },
  }), [redemptionByMonthAxis.max, redemptionByMonthAxis.stepSize]);

  const buildSmokerProfileChartData = useCallback((company: SmokerProfileCompany) => {
    const brands = company.brands ?? [];
    const categories = company.categories ?? [];
    const brandColors = brands.map((_, i) => COLORS[i % COLORS.length]);
    const categoryColors = categories.map((_, i) => COLORS[(i + 6) % COLORS.length]);
    return {
      brandLegend: brands.map((p, i) => ({ ...p, color: brandColors[i] })),
      categoryLegend: categories.map((p, i) => ({ ...p, color: categoryColors[i] })),
      brandData: {
        labels: brands.map((p) => p.label),
        datasets: [
          {
            label: 'BAT Brands',
            data: brands.map((p) => p.count),
            backgroundColor: brandColors,
            borderColor: brandColors,
            borderWidth: 1.5,
            hoverOffset: 4,
          },
        ],
      },
      categoryData: {
        labels: categories.map((p) => p.label),
        datasets: [
          {
            label: 'BAT Categories',
            data: categories.map((p) => p.count),
            backgroundColor: categoryColors,
            borderColor: categoryColors,
            borderWidth: 1.2,
            hoverOffset: 4,
          },
        ],
      },
    };
  }, []);

  const smokerProfileOuterOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    cutout: '60%',
    layout: {
      // Leave extra room so outer donut labels are not clipped.
      padding: { top: 20, right: 34, bottom: 20, left: 34 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 9, weight: 700 as const },
        anchor: 'end' as const,
        align: 'end' as const,
        offset: 10,
        clamp: false,
        clip: false,
        backgroundColor: 'rgba(255,255,255,0.88)',
        borderRadius: 3,
        padding: { top: 2, right: 3, bottom: 2, left: 3 },
        textStrokeColor: '#ffffff',
        textStrokeWidth: 1.5,
        formatter: (value: number, ctx: any) => {
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, smokerProfileLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, smokerProfileLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [smokerProfileLabelMode]);

  const smokerProfileInnerOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 8, weight: 700 as const },
        clamp: true,
        clip: false,
        formatter: (value: number, ctx: any) => {
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, smokerProfileLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, smokerProfileLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [smokerProfileLabelMode]);

  const distData = useMemo(() => {
    const d   = stats?.redemptionByDistributor ?? [];
    const dists = stats?.distributors ?? [];
    return {
      labels: d.map((p) => p.date as string),
      datasets: dists.map((dist, idx) => ({
        label: dist,
        data: d.map((p) => (p[dist] as number) ?? 0),
        backgroundColor: COLORS[idx % COLORS.length],
        borderColor: COLORS[idx % COLORS.length],
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
        stack: 'a',
      })),
    };
  }, [stats?.redemptionByDistributor, stats?.distributors]);

  const distOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: {
        ...BASE_OPTIONS.plugins.legend,
        position: 'right' as const,
        labels: {
          ...BASE_OPTIONS.plugins.legend.labels,
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 9 },
          padding: 6,
        },
      },
      datalabels: { ...STACKED_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: { ...BASE_OPTIONS.scales.x, stacked: true },
      y: { ...BASE_OPTIONS.scales.y, stacked: true, beginAtZero: true, grace: '15%' },
    },
  }), []);

  const ageData = useMemo(() => {
    const d = stats?.ageDistribution ?? [];
    return {
      labels: d.map((p) => p.age),
      datasets: [{
        label: 'Count',
        data: d.map((p) => p.count),
        backgroundColor: '#93c5fd',
        borderColor: '#60a5fa',
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.ageDistribution]);

  const ageAxis = useMemo(
    () => buildNiceAxis((stats?.ageDistribution ?? []).map((p) => p.count)),
    [stats?.ageDistribution]
  );

  const ageOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: {
        ...BAR_DATALABELS,
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          if (ageLabelMode === 'value') return NUMBER_FORMATTER.format(value);
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, 'percentage');
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed?.y ?? ctx.parsed ?? 0;
            if (ageLabelMode === 'value') return ` ${ctx.label}: ${NUMBER_FORMATTER.format(value)}`;
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            return ` ${ctx.label}: ${formatPieLabel(value, total, 'percentage')}`;
          },
        },
      },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      y: {
        ...BASE_OPTIONS.scales.y,
        beginAtZero: true,
        max: ageAxis.max,
        ticks: {
          ...BASE_OPTIONS.scales.y.ticks,
          stepSize: ageAxis.stepSize,
          callback: formatNumberTick,
        },
      },
    },
  }), [ageAxis.max, ageAxis.stepSize, ageLabelMode]);

  const stateRaceData = useMemo(() => {
    const d     = stats?.redemptionByStateRace ?? [];
    const races = stats?.races ?? [];
    return {
      labels: d.map((p) => p.state as string),
      datasets: races.map((race, idx) => ({
        label: race,
        data: d.map((p) => (p[race] as number) ?? 0),
        backgroundColor: COLORS[idx % COLORS.length],
        borderColor: COLORS[idx % COLORS.length],
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 16,
        maxBarThickness: 20,
        stack: 'a',
      })),
    };
  }, [stats?.redemptionByStateRace, stats?.races]);

  const stateRaceOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    indexAxis: 'y' as const,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: {
        ...BASE_OPTIONS.plugins.legend,
        position: 'top' as const,
        labels: {
          ...BASE_OPTIONS.plugins.legend.labels,
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 9 },
          padding: 6,
        },
      },
      datalabels: { ...STACKED_DATALABELS, font: { size: 7, weight: 600 as const } },
    },
    scales: {
      x: {
        stacked: true,
        grace: '15%',
        ticks: { font: { size: 9 } },
        grid: { color: 'rgba(148,163,184,0.25)', borderDash: [2, 2] as [number, number] },
        border: { display: false },
      },
      y: {
        stacked: true,
        ticks: { font: { size: 9 }, padding: 4 },
        grid: { display: false },
        border: { display: false },
        offset: true,
      },
    },
  }), []);

  const redemptionByStateData = useMemo(() => {
    const d = stats?.redemptionByStateRace ?? [];
    const races = stats?.races ?? [];
    const labels = d.map((p) => p.state as string);
    const totals = d.map((p) =>
      races.reduce((sum, race) => sum + ((p[race] as number) ?? 0), 0)
    );
    return {
      labels,
      datasets: [{
        label: 'Count',
        data: totals,
        backgroundColor: '#93c5fd',
        borderColor: '#60a5fa',
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.redemptionByStateRace, stats?.races]);

  const redemptionByStateAxis = useMemo(
    () => buildNiceAxis((redemptionByStateData.datasets?.[0]?.data as number[]) ?? []),
    [redemptionByStateData]
  );

  const redemptionByStateOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
      },
      y: {
        ...BASE_OPTIONS.scales.y,
        beginAtZero: true,
        max: redemptionByStateAxis.max,
        ticks: {
          ...BASE_OPTIONS.scales.y.ticks,
          stepSize: redemptionByStateAxis.stepSize,
          callback: formatNumberTick,
        },
      },
    },
  }), [redemptionByStateAxis.max, redemptionByStateAxis.stepSize]);

  const redemptionByRaceData = useMemo(() => {
    const d = stats?.redemptionByStateRace ?? [];
    const races = stats?.races ?? [];
    const totals = races.map((race) =>
      d.reduce((sum, row) => sum + ((row[race] as number) ?? 0), 0)
    );
    const colors = races.map((_, i) => COLORS[i % COLORS.length]);
    return {
      labels: races,
      legend: races.map((race, i) => ({ label: race, count: totals[i], color: colors[i] })),
      datasets: [{
        label: 'Count',
        data: totals,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.redemptionByStateRace, stats?.races]);

  const redemptionByRaceOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 10, weight: 700 as const },
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, ethnicLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, ethnicLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [ethnicLabelMode]);

  const unlimitedPuffData = useMemo(() => {
    const d = stats?.unlimitedPuffFeedback ?? { convenientRelevant: 0, notRelevant: 0 };
    const labels = ['Convenient and Relevant', 'Not Relevant'];
    const data = [d.convenientRelevant, d.notRelevant];
    const colors = ['#86efac', '#fca5a5'];
    return {
      labels,
      legend: labels.map((label, i) => ({ label, count: data[i], color: colors[i] })),
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: ['#4ade80', '#f87171'],
        borderWidth: 1,
      }],
    };
  }, [stats?.unlimitedPuffFeedback]);

  const unlimitedPuffOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 10, weight: 700 as const },
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, unlimitedPuffLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, unlimitedPuffLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [unlimitedPuffLabelMode]);

  const hasUnlimitedPuffData = useMemo(() => {
    const d = stats?.unlimitedPuffFeedback;
    if (!d) return false;
    return d.convenientRelevant + d.notRelevant > 0;
  }, [stats?.unlimitedPuffFeedback]);

  const deviceRegFeedbackData = useMemo(() => {
    const rows = stats?.deviceRegistrationFeedback ?? [];
    const labels = rows.map((row) => row.label);
    const data = rows.map((row) => row.count);
    const colors = rows.map((_, i) => COLORS[i % COLORS.length]);
    return {
      labels,
      legend: rows.map((row, i) => ({ ...row, color: colors[i] })),
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    };
  }, [stats?.deviceRegistrationFeedback]);

  const hasDeviceRegFeedbackData = useMemo(() => {
    return deviceRegFeedbackData.datasets[0].data.reduce((a, b) => a + b, 0) > 0;
  }, [deviceRegFeedbackData]);

  const deviceRegFeedbackOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 10, weight: 700 as const },
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, deviceRegLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, deviceRegLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [deviceRegLabelMode]);

  const ploomExperienceData = useMemo(() => {
    const rows = stats?.ploomExperience ?? [];
    const labels = rows.map((row) => row.label);
    const data = rows.map((row) => row.count);
    const colors = rows.map((_, i) => COLORS[i % COLORS.length]);
    return {
      labels,
      legend: rows.map((row, i) => ({ ...row, color: colors[i] })),
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    };
  }, [stats?.ploomExperience]);

  const hasPloomExperienceData = useMemo(() => {
    return ploomExperienceData.datasets[0].data.reduce((a, b) => a + b, 0) > 0;
  }, [ploomExperienceData]);

  const ploomExperienceOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500 },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: '#1e293b',
        font: { size: 10, weight: 700 as const },
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, ploomExperienceLabelMode);
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            const formatted = formatPieLabel(ctx.parsed, total, ploomExperienceLabelMode);
            return ` ${ctx.label}: ${formatted}`;
          },
        },
      },
    },
  }), [ploomExperienceLabelMode]);

  const oosAreaRows = useMemo(() => stats?.oosAreaCounts ?? [], [stats?.oosAreaCounts]);
  const groupedOosAreaRows = useMemo(() => {
    const grouped = new Map<number, string[]>();
    for (const row of oosAreaRows) {
      if (!grouped.has(row.count)) grouped.set(row.count, []);
      grouped.get(row.count)!.push(row.zone);
    }
    return [...grouped.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([count, zones]) => ({
        count,
        zones: zones.sort((a, b) => a.localeCompare(b)),
      }));
  }, [oosAreaRows]);

  const consumerEvoDailyUsageData = useMemo(() => {
    const rows = stats?.consumerEvoDailyUsage ?? [];
    const colors = ['#60a5fa', '#34d399', '#f59e0b', '#f472b6'];
    return {
      labels: rows.map((row) => row.label),
      datasets: [{
        label: 'Count',
        data: rows.map((row) => row.count),
        backgroundColor: rows.map((_, i) => colors[i % colors.length]),
        borderColor: rows.map((_, i) => colors[i % colors.length]),
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 21,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.consumerEvoDailyUsage]);

  const hasConsumerEvoDailyUsageData = useMemo(() => {
    return consumerEvoDailyUsageData.datasets[0].data.reduce((a, b) => a + b, 0) > 0;
  }, [consumerEvoDailyUsageData]);

  const consumerEvoDailyUsageOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    indexAxis: 'y' as const,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: {
        ...BAR_DATALABELS,
        anchor: 'end' as const,
        align: 'right' as const,
        formatter: (value: number, ctx: any) => {
          if (!value) return '';
          if (evoUsageLabelMode === 'value') return NUMBER_FORMATTER.format(value);
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return formatPieLabel(value, total, 'percentage');
        },
      },
      tooltip: {
        bodyFont: { size: 11 },
        titleFont: { size: 11 },
        padding: 8,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed?.x ?? ctx.parsed ?? 0;
            if (evoUsageLabelMode === 'value') return ` ${ctx.label}: ${NUMBER_FORMATTER.format(value)}`;
            const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
            return ` ${ctx.label}: ${formatPieLabel(value, total, 'percentage')}`;
          },
        },
      },
    },
    scales: {
      x: {
        ...BASE_OPTIONS.scales.x,
        beginAtZero: true,
        grace: '15%',
        ticks: {
          ...BASE_OPTIONS.scales.x.ticks,
          callback: formatNumberTick,
        },
      },
      y: {
        ...BASE_OPTIONS.scales.y,
        ticks: { ...BASE_OPTIONS.scales.y.ticks, font: { size: 9 } },
      },
    },
  }), [evoUsageLabelMode]);

  // ── OOS Experience ────────────────────────────────────────────────────
  const oosExperienceData = useMemo(() => {
    const d = stats?.oosExperience ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#fcd34d',
        borderColor: '#fbbf24',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.oosExperience]);

  const oosExperienceOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── EVO Consumption Count ─────────────────────────────────────────────
  const evoConsumptionData = useMemo(() => {
    const d = stats?.evoConsumption ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#86efac',
        borderColor: '#4ade80',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.evoConsumption]);

  const evoConsumptionOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: { ...BASE_OPTIONS.scales.x.ticks, maxRotation: 45, minRotation: 30 },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── Key Account Redemption Count ──────────────────────────────────────
  const keyAccountData = useMemo(() => {
    const d = stats?.keyAccountRedemption ?? [];
    return {
      labels: d.map((p: { label: string; count: number }) => p.label),
      datasets: [{
        label: 'Count',
        data: d.map((p: { label: string; count: number }) => p.count),
        backgroundColor: '#93c5fd',
        borderColor: '#60a5fa',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 20,
        maxBarThickness: 24,
      }],
    };
  }, [stats?.keyAccountRedemption]);

  const keyAccountOptions = useMemo(() => ({
    ...BASE_OPTIONS,
    plugins: {
      ...BASE_OPTIONS.plugins,
      legend: { display: false },
      datalabels: { ...BAR_DATALABELS },
    },
    scales: {
      ...BASE_OPTIONS.scales,
      x: {
        ...BASE_OPTIONS.scales.x,
        ticks: {
          ...BASE_OPTIONS.scales.x.ticks,
          maxRotation: 45,
          minRotation: 30,
        },
      },
      y: { ...BASE_OPTIONS.scales.y, beginAtZero: true, grace: '15%', ticks: { ...BASE_OPTIONS.scales.y.ticks, stepSize: 1 } },
    },
  }), []);

  // ── Render ──────────────────────────────────────────────────────────────

  const chartBox = 'box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-3 h-[420px] shadow-sm flex flex-col';
  const chartTitle = 'text-sm font-semibold text-slate-600 dark:text-slate-300 text-left mb-2';
  const loadingEl  = <div className="flex-1 flex items-center justify-center text-slate-400 text-sm animate-pulse">Loading…</div>;
  const showLoadingState = loading && !stats;
  const contactMetricLabel =
    contactMode === 'both'
      ? 'Total Data Collected:'
      : contactMode === 'active'
        ? 'Active Consumer Collected:'
        : 'Unique Consumer Collected:';
  const contactMetricValue =
    contactMode === 'both'
      ? (stats?.totalCustomers ?? 0)
      : contactMode === 'active'
        ? (stats?.activeContactCount ?? 0)
        : (stats?.uniqueContactCount ?? 0);

  return (
    <div className="p-6">

      {/* ── Toolbar ── */}
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative inline-flex">
            <Lucide icon="Calendar" className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]" />
            <Litepicker
              value={dateRange}
              onChange={(e) => setDateRange((e.target?.value || '').trim())}
              options={{ autoApply: true, singleMode: false, numberOfColumns: 2, numberOfMonths: 2, showWeekNumbers: true, dropdowns: { minYear: 2024, maxYear: null, months: true, years: true }, format: 'YYYY-MM-DD' }}
              className="w-[270px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
            />
          </div>
          <div className="inline-flex items-center gap-3 rounded-[0.5rem] px-3 py-2 border border-white/20 bg-white/[0.08] text-xs text-white/95">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                name="contactMode"
                className="h-3.5 w-3.5"
                checked={isUniqueSelected}
                onChange={(e) => handleUniqueToggle(e.target.checked)}
              />
              <span>Unique Consumer</span>
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                name="contactMode"
                className="h-3.5 w-3.5"
                checked={isActiveSelected}
                onChange={(e) => handleActiveToggle(e.target.checked)}
              />
              <span>Active Consumer</span>
            </label>
          </div>
          <Button variant="primary" onClick={handleSearch} disabled={!dateRangeISO || loading} className="w-full sm:w-auto">
            {loading
              ? <><Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />Loading…</>
              : <><Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />Search</>}
          </Button>
          <Button
            variant="primary"
            onClick={handleExportToPdf}
            disabled={loading || isExportingPdf || !stats}
            className="w-full sm:w-auto font-semibold shadow-md"
          >
            {isExportingPdf
              ? <><Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />Exporting…</>
              : <><Lucide icon="FileDown" className="stroke-[1.3] w-4 h-4 mr-2" />Export to PDF</>}
          </Button>
        </div>
        <div className="inline-flex items-center text-xs text-white/95">
          {isRefreshing ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-3.5 h-3.5 mr-2 animate-spin" />
              Refreshing dashboard...
            </>
          ) : lastUpdatedAt ? (
            <>
              <Lucide icon="Clock3" className="stroke-[1.3] w-3.5 h-3.5 mr-2" />
              Last updated at {dayjs(lastUpdatedAt).format('HH:mm:ss')}
            </>
          ) : null}
        </div>
      </div>

      {/* ── Error ── */}
      {error && <div className="mb-6 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">{error}</div>}

      <div ref={dashboardExportRef} className="space-y-5">
        {/* ── Row 1: summary stats ── */}
        <div className="mb-5" data-pdf-block="true">
          <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm flex items-center gap-0 divide-x divide-slate-200 dark:divide-darkmode-400">
            <div className="flex flex-1 items-center justify-center gap-3 pr-6">
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">{contactMetricLabel}</p>
              <p className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100 flex-shrink-0">
                {showLoadingState ? <span className="text-xl text-slate-400 animate-pulse">…</span> : contactMetricValue.toLocaleString()}
              </p>
            </div>
            <div className="flex flex-1 items-center justify-center pl-6">
              <div className="inline-flex flex-col items-start gap-1">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">TnG Redemption Count:</p>
                  {showLoadingState
                    ? <span className="text-xl text-slate-400 animate-pulse flex-shrink-0">…</span>
                    : (
                      <div className="flex items-baseline gap-2 flex-shrink-0 whitespace-nowrap">
                        <span className="text-3xl font-bold tabular-nums text-slate-800 dark:text-slate-100">{(stats?.tngUsed ?? 0).toLocaleString()}</span>
                        <span className="text-lg font-semibold text-slate-400 dark:text-slate-500">/ {(stats?.tngTotal ?? 0).toLocaleString()}</span>
                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">({stats?.tngPct ?? 0}% Used)</span>
                      </div>
                    )
                  }
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Since 9th May 2025</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="grid grid-cols-1 gap-5">

        {/* [1] Redemption by Month */}
        <div className={chartBox} data-pdf-block="true">
          <p className={chartTitle}>Redemption by Month</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={redemptionByMonthData} options={redemptionByMonthOptions} /></div>}
        </div>

        {/* [0] Unique Consumer & Active Consumer */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>Unique Consumer &amp; Active Consumer</p>
          {showLoadingState ? loadingEl : (
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4">
              <div className="flex-1 min-h-0 flex flex-col">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 text-center mb-2">Unique Consumer</p>
                <div className="flex-1 min-h-0">
                  <Chart type="bar" data={newUsersByWeekData} options={weeklyUsersBarOptions} />
                </div>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 text-center mb-2">Active Consumer</p>
                <div className="flex-1 min-h-0">
                  <Chart type="bar" data={existingUsersByWeekData} options={weeklyUsersBarOptions} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* [2] Smoker Profile */}
        <div className={`${chartBox} h-auto min-h-[420px]`} data-pdf-block="true">
          <div className="flex items-center justify-between mb-2">
            <p className={`${chartTitle} mb-0`}>Smoker Profile</p>
            <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
              <button
                type="button"
                onClick={() => setSmokerProfileLabelMode('percentage')}
                className={`px-2 py-1 rounded ${
                  smokerProfileLabelMode === 'percentage'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => setSmokerProfileLabelMode('value')}
                className={`px-2 py-1 rounded ${
                  smokerProfileLabelMode === 'value'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                Value
              </button>
            </div>
          </div>
          {showLoadingState
            ? loadingEl
            : (() => {
                return (
                  <div className="flex-1 min-h-0">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {(stats?.smokerProfileByCompany ?? []).map((company) => {
                        const chartData = buildSmokerProfileChartData(company);
                        const brandTotal = chartData.brandLegend.reduce((sum, item) => sum + item.count, 0);
                        const categoryTotal = chartData.categoryLegend.reduce((sum, item) => sum + item.count, 0);
                        return (
                          <div
                            key={company.company}
                            className="rounded-lg border border-slate-200/70 dark:border-darkmode-400 p-3 min-h-[300px] flex items-center"
                          >
                            <div className="w-[68%] h-full flex flex-col items-center">
                              <p className="text-lg font-bold text-slate-700 dark:text-slate-100 text-center mb-2">
                                {company.company}
                              </p>
                              <div className="relative w-full flex-1 flex items-center justify-center">
                                <div className="relative w-[340px] h-[290px] max-w-full overflow-visible">
                                  <div className="absolute inset-0">
                                    <Chart type="doughnut" data={chartData.brandData} options={smokerProfileOuterOptions} />
                                  </div>
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-[40%] h-[40%] pointer-events-auto">
                                      <Chart type="pie" data={chartData.categoryData} options={smokerProfileInnerOptions} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="w-[32%] flex flex-col justify-center gap-2 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                              <div>
                                <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">{company.company} Brand</p>
                                {chartData.brandLegend.map((item) => (
                                  <div key={`${company.company}-brand-${item.label}`} className="flex items-center gap-1 min-w-0">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                                    <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65] whitespace-nowrap">
                                      {item.label}: {formatLegendMetric(item.count, brandTotal, smokerProfileLabelMode)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">{company.company} Category</p>
                                {chartData.categoryLegend.map((item) => (
                                  <div key={`${company.company}-category-${item.label}`} className="flex items-center gap-1 min-w-0">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                                    <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65] whitespace-nowrap">
                                      {item.label}: {formatLegendMetric(item.count, categoryTotal, smokerProfileLabelMode)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
          }
        </div>

        {/* [3] Redemption by Distributor */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>Redemption by Distributor</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={distData} options={distOptions} /></div>}
        </div>

        {/* [4] Age Range */}
        <div className={chartBox} data-pdf-block="true">
          <div className="flex items-center justify-between mb-2">
            <p className={`${chartTitle} mb-0`}>Age Range</p>
            <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
              <button
                type="button"
                onClick={() => setAgeLabelMode('percentage')}
                className={`px-2 py-1 rounded ${
                  ageLabelMode === 'percentage'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => setAgeLabelMode('value')}
                className={`px-2 py-1 rounded ${
                  ageLabelMode === 'value'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                Value
              </button>
            </div>
          </div>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={ageData} options={ageOptions} /></div>}
        </div>

        {/* [5] Smoker's State of Residence */}
        <div className={chartBox} data-pdf-block="true">
          <p className={chartTitle}>Smoker&apos;s State of Residence</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={redemptionByStateData} options={redemptionByStateOptions} /></div>}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* [5.1] Smoker Ethnic Breakdown */}
          <div className={chartBox} data-pdf-block="true">
            <div className="flex items-center justify-between mb-2">
              <p className={`${chartTitle} mb-0`}>Smoker Ethnic Breakdown</p>
              <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setEthnicLabelMode('percentage')}
                  className={`px-2 py-1 rounded ${
                    ethnicLabelMode === 'percentage'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => setEthnicLabelMode('value')}
                  className={`px-2 py-1 rounded ${
                    ethnicLabelMode === 'value'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  Value
                </button>
              </div>
            </div>
            {showLoadingState ? loadingEl : (
              <div className="flex-1 min-h-0 flex items-center">
                <div className="w-[72%] h-full flex items-center justify-center">
                  <div className="w-[280px] h-[280px] max-w-full">
                    <Chart type="pie" data={redemptionByRaceData} options={redemptionByRaceOptions} />
                  </div>
                </div>
                <div className="w-[28%] flex flex-col justify-center gap-1.5 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                  {redemptionByRaceData.legend.map((item) => (
                    <div key={`ethnic-${item.label}`} className="flex items-center gap-1 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                      <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65]">
                        {item.label}: {formatLegendMetric(
                          item.count,
                          redemptionByRaceData.datasets[0].data.reduce((a, b) => a + b, 0),
                          ethnicLabelMode
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* [5.3] Legacy only: Device registration feedback */}
          <div className={chartBox} data-pdf-block="true">
            <div className="flex items-center justify-between mb-2">
              <p className={`${chartTitle} mb-0`}>Consumer feedback on NPD device registration</p>
              <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setDeviceRegLabelMode('percentage')}
                  className={`px-2 py-1 rounded ${
                    deviceRegLabelMode === 'percentage'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => setDeviceRegLabelMode('value')}
                  className={`px-2 py-1 rounded ${
                    deviceRegLabelMode === 'value'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  Value
                </button>
              </div>
            </div>
            {showLoadingState ? loadingEl : (
              hasDeviceRegFeedbackData
                ? (
                  <div className="flex-1 min-h-0 flex items-center">
                    <div className="w-[72%] h-full flex items-center justify-center">
                      <div className="w-[280px] h-[280px] max-w-full">
                        <Chart type="pie" data={deviceRegFeedbackData} options={deviceRegFeedbackOptions} />
                      </div>
                    </div>
                    <div className="w-[28%] flex flex-col justify-center gap-1.5 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                      {deviceRegFeedbackData.legend.map((item) => (
                        <div key={`device-reg-${item.label}`} className="flex items-center gap-1 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                          <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65]">
                            {item.label}: {formatLegendMetric(
                              item.count,
                              deviceRegFeedbackData.datasets[0].data.reduce((a, b) => a + b, 0),
                              deviceRegLabelMode
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
                : <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">No past-data records in selected date range.</div>
            )}
          </div>
        </div>

        {/* [5.4] Legacy only: Consumer EVO stick daily usage */}
        <div className={chartBox} data-pdf-block="true">
          <div className="flex items-center justify-between mb-2">
            <p className={`${chartTitle} mb-0`}>Consumer EVO stick daily usage</p>
            <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
              <button
                type="button"
                onClick={() => setEvoUsageLabelMode('percentage')}
                className={`px-2 py-1 rounded ${
                  evoUsageLabelMode === 'percentage'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => setEvoUsageLabelMode('value')}
                className={`px-2 py-1 rounded ${
                  evoUsageLabelMode === 'value'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                Value
              </button>
            </div>
          </div>
          {showLoadingState ? loadingEl : (
            hasConsumerEvoDailyUsageData
              ? <div className="flex-1 min-h-0"><Chart type="bar" data={consumerEvoDailyUsageData} options={consumerEvoDailyUsageOptions} /></div>
              : <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">No past-data records in selected date range.</div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* [5.2] Legacy only: Unlimited Puff feedback */}
          <div className={chartBox} data-pdf-block="true">
            <div className="flex items-center justify-between mb-2">
              <p className={`${chartTitle} mb-0`}>Consumer feedback on the unlimited puff feature in NPD</p>
              <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setUnlimitedPuffLabelMode('percentage')}
                  className={`px-2 py-1 rounded ${
                    unlimitedPuffLabelMode === 'percentage'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => setUnlimitedPuffLabelMode('value')}
                  className={`px-2 py-1 rounded ${
                    unlimitedPuffLabelMode === 'value'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  Value
                </button>
              </div>
            </div>
            {showLoadingState ? loadingEl : (
              hasUnlimitedPuffData
                ? (
                  <div className="flex-1 min-h-0 flex items-center">
                    <div className="w-[72%] h-full flex items-center justify-center">
                      <div className="w-[280px] h-[280px] max-w-full">
                        <Chart type="pie" data={unlimitedPuffData} options={unlimitedPuffOptions} />
                      </div>
                    </div>
                    <div className="w-[28%] flex flex-col justify-center gap-1.5 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                      {unlimitedPuffData.legend.map((item) => (
                        <div key={`unlimited-puff-${item.label}`} className="flex items-center gap-1 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                          <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65]">
                            {item.label}: {formatLegendMetric(
                              item.count,
                              unlimitedPuffData.datasets[0].data.reduce((a, b) => a + b, 0),
                              unlimitedPuffLabelMode
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
                : <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">No past-data records in selected date range.</div>
            )}
          </div>

          {/* [9] Legacy only: NPD Experience */}
          <div className={chartBox} data-pdf-block="true">
            <div className="flex items-center justify-between mb-2">
              <p className={`${chartTitle} mb-0`}>NPD Experience</p>
              <div className="inline-flex items-center gap-1 rounded-md border border-slate-300/70 dark:border-darkmode-400 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setPloomExperienceLabelMode('percentage')}
                  className={`px-2 py-1 rounded ${
                    ploomExperienceLabelMode === 'percentage'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => setPloomExperienceLabelMode('value')}
                  className={`px-2 py-1 rounded ${
                    ploomExperienceLabelMode === 'value'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  Value
                </button>
              </div>
            </div>
            {showLoadingState ? loadingEl : (
              hasPloomExperienceData
                ? (
                  <div className="flex-1 min-h-0 flex items-center">
                    <div className="w-[72%] h-full flex items-center justify-center">
                      <div className="w-[280px] h-[280px] max-w-full">
                        <Chart type="pie" data={ploomExperienceData} options={ploomExperienceOptions} />
                      </div>
                    </div>
                    <div className="w-[28%] flex flex-col justify-center gap-1.5 pl-2 overflow-y-auto max-h-full flex-shrink-0">
                      {ploomExperienceData.legend.map((item) => (
                        <div key={`ploom-experience-${item.label}`} className="flex items-center gap-1 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                          <span className="text-[11px] text-slate-600 dark:text-slate-400 leading-[1.65]">
                            {item.label}: {formatLegendMetric(
                              item.count,
                              ploomExperienceData.datasets[0].data.reduce((a, b) => a + b, 0),
                              ploomExperienceLabelMode
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
                : <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">No past-data records in selected date range.</div>
            )}
          </div>
        </div>

        {/* [6] Key Account Redemption Count */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>Key Account Redemption Count</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={keyAccountData} options={keyAccountOptions} /></div>}
        </div>

        {/* [7] EVO Consumption Count */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>EVO Consumption Count</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={evoConsumptionData} options={evoConsumptionOptions} /></div>}
        </div>

        {/* [8] OOS Experience */}
        <div className={`${chartBox} hidden`}>
          <p className={chartTitle}>OOS Experience</p>
          {showLoadingState ? loadingEl : <div className="flex-1 min-h-0"><Chart type="bar" data={oosExperienceData} options={oosExperienceOptions} /></div>}
        </div>

        {/* [10] OOS Area */}
        <div className={`${chartBox} h-auto min-h-[420px]`} data-pdf-block="true">
          <p className={chartTitle}>OOS Area</p>
          {showLoadingState ? loadingEl : (
            oosAreaRows.length > 0 ? (
              <div className="flex-1 min-h-0 overflow-x-auto">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="text-right px-4 py-3 bg-slate-100 dark:bg-darkmode-600 text-slate-700 dark:text-slate-200 font-semibold border-r border-slate-300/80 dark:border-darkmode-400 rounded-tr-md">
                        No. of OOS submitted
                      </th>
                      <th className="text-left px-4 py-3 bg-slate-100 dark:bg-darkmode-600 text-slate-700 dark:text-slate-200 font-semibold rounded-tl-md">
                        Areas (grouped by same count)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedOosAreaRows.map((row, idx) => (
                      <tr key={`oos-area-group-${row.count}-${idx}`}>
                        <td className="px-4 py-3 align-middle border-b border-r border-slate-200/80 dark:border-darkmode-400 text-center font-bold text-xl text-slate-800 dark:text-slate-100">
                          {row.count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border-b border-slate-200/80 dark:border-darkmode-400 text-slate-700 dark:text-slate-200">
                          <div className="columns-1 md:columns-2 xl:columns-3 gap-6">
                            {row.zones.map((zone) => (
                              <div key={`${row.count}-${zone}`} className="break-inside-avoid py-0.5">
                                {zone}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                No Customer.zone records in selected date range.
              </div>
            )
          )}
        </div>

        </div>
      </div>
    </div>
  );
}
